import { useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { useMe } from "@aeriosiq/auth-client";
import type {
  AssigneeCandidate,
  GetAssigneeCandidatesResponse,
} from "@aeriosiq/types";
import { ASSIGNEE_UNASSIGNED } from "./filters.js";

/**
 * Theme-A polish (2026-05-08) — multi-select Assignee filter with two
 * pseudo-rows pinned at the top of the dropdown:
 *   1. **Assigned to me** — when picked, the URL gets the current
 *      user's UUID (resolved via `useMe()`); displays as the
 *      pseudo-label in the dropdown but stores as a regular UUID so
 *      shareable links don't depend on who's signed in.
 *   2. **Unassigned** — sentinel value `__unassigned__` that the
 *      dev-api / BE maps to `assigned_to IS NULL`.
 *
 * Real assignees come from `GET /data-acquisitions/{id}/assignee-candidates`,
 * but this filter is at the list level (no inspection ID), so we use a
 * sentinel inspection id to ask for the synth roster. When the BE picks
 * up FEAT-001 they'll add a `/assignees?q=…` route; until then this
 * route serves both purposes.
 */
const PSEUDO_ME_ID = "__me__";
const PSEUDO_UNASSIGNED_ID = ASSIGNEE_UNASSIGNED;

export interface AssigneeFilterProps {
  value: string[];
  onChange: (next: string[]) => void;
}

export function AssigneeFilter({ value, onChange }: AssigneeFilterProps) {
  const api = useApi();
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const candidatesQuery = useQuery<GetAssigneeCandidatesResponse>({
    queryKey: ["assignee-candidates", "list-filter", search],
    enabled: open,
    queryFn: async () => {
      // Sentinel id — the dev-api route ignores `:id` and returns the
      // tenant-wide roster.
      const response = await api.get<GetAssigneeCandidatesResponse>(
        `/data-acquisitions/00000000-0000-0000-0000-000000000000/assignee-candidates`,
        { params: search ? { q: search } : {} },
      );
      return response.data;
    },
    staleTime: 30_000,
  });

  // Pseudo-row factories. "Assigned to me" is only shown when we have
  // a current-user UUID to resolve to; otherwise we hide it (a logged-
  // out viewer wouldn't have a meaningful "me" anyway). Both wrapped in
  // useMemo so their object identity is stable across renders — the
  // downstream `options` / `selectedObjects` useMemos depend on them.
  const meId = me.data?.id;
  const meInitials = me.data?.initials;
  const meTenantId = me.data?.tenantId;
  const meCandidate = useMemo<AssigneeCandidate | null>(
    () =>
      meId
        ? {
            id: PSEUDO_ME_ID, // re-mapped to meId on selection
            displayName: "Assigned to me",
            initials: meInitials ?? "ME",
            tenantId: meTenantId ?? "00000000-0000-0000-0000-000000000000",
            tenantSlug: "",
            tenantDisplayName: null,
            sameTenant: true,
          }
        : null,
    [meId, meInitials, meTenantId],
  );
  const unassignedCandidate = useMemo<AssigneeCandidate>(
    () => ({
      id: PSEUDO_UNASSIGNED_ID,
      displayName: "Unassigned",
      initials: "—",
      tenantId: "00000000-0000-0000-0000-000000000000",
      tenantSlug: "",
      tenantDisplayName: null,
      sameTenant: true,
    }),
    [],
  );

  // Build the option list. Pseudo rows always come first; the live
  // candidate roster from /assignee-candidates fills the rest.
  const options = useMemo<AssigneeCandidate[]>(() => {
    const head: AssigneeCandidate[] = [];
    if (meCandidate) head.push(meCandidate);
    head.push(unassignedCandidate);
    return [...head, ...(candidatesQuery.data?.items ?? [])];
  }, [meCandidate, unassignedCandidate, candidatesQuery.data?.items]);

  const selectedObjects = useMemo<AssigneeCandidate[]>(() => {
    return value.map((id): AssigneeCandidate => {
      if (id === PSEUDO_UNASSIGNED_ID) return unassignedCandidate;
      // If id matches the current user's UUID, render as "Assigned to me".
      if (meId && id === meId && meCandidate)
        return { ...meCandidate, id: meId };
      // Otherwise, find by id from the roster, or render as a UUID stub.
      const fromRoster = candidatesQuery.data?.items.find((c) => c.id === id);
      return (
        fromRoster ?? {
          id,
          displayName: id,
          initials: "?",
          tenantId: "00000000-0000-0000-0000-000000000000",
          tenantSlug: "",
          tenantDisplayName: null,
          sameTenant: true,
        }
      );
    });
  }, [
    value,
    meId,
    meCandidate,
    unassignedCandidate,
    candidatesQuery.data?.items,
  ]);

  const handleChange = (_e: unknown, next: AssigneeCandidate[]) => {
    // Translate the "Assigned to me" pseudo selection into the actual
    // current-user UUID so the URL is shareable. Skip the pseudo row
    // entirely if `meId` is unknown (defensive).
    onChange(
      next
        .map((c) => {
          if (c.id === PSEUDO_ME_ID) return meId ?? null;
          return c.id;
        })
        .filter((id): id is string => id !== null),
    );
  };

  return (
    <Autocomplete<AssigneeCandidate, true>
      multiple
      disableCloseOnSelect
      size="small"
      open={open}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
        // Clear the typed search string on close so reopening shows
        // the full top-50 again instead of the previous narrowed slice.
        // Mirrors the Status filter ergonomic (60_TEMPLATE_DATA_PAGE.md).
        setSearch("");
      }}
      options={options}
      value={selectedObjects}
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.displayName}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={candidatesQuery.isPending && open}
      onChange={(e, next) => {
        handleChange(e, next);
        // Match Status filter: after picking, reset the typed query so
        // the dropdown immediately re-shows the full option list (the
        // popup stays open via `disableCloseOnSelect`).
        setSearch("");
      }}
      onInputChange={(_e, v, reason) => {
        if (reason === "input") setSearch(v);
      }}
      // Fixed width — Christian, 2026-05-13. Sized so a single typical
      // assignee chip plus the MUI input-cursor min-width (~80 px) tile
      // on ONE line; the field grows in height only when 2+ chips
      // force a wrap.
      sx={{ width: 280 }}
      renderOption={(props, option) => {
        const liProps = props as React.HTMLAttributes<HTMLLIElement> & {
          key?: string;
        };
        const { key: liKey, ...restLi } = liProps;
        const isPseudo =
          option.id === PSEUDO_ME_ID || option.id === PSEUDO_UNASSIGNED_ID;
        return (
          <Box
            component="li"
            key={liKey ?? option.id}
            {...restLi}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontStyle: isPseudo ? "italic" : "normal",
            }}
          >
            <Typography variant="body2">{option.displayName}</Typography>
          </Box>
        );
      }}
      renderInput={(p) => {
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        const tfProps = {
          ...rest,
          label: "Assignee",
          slotProps: {
            htmlInput: inputProps,
            input: {
              ...InputProps,
              endAdornment: (
                <>
                  {candidatesQuery.isPending && open ? (
                    <CircularProgress size={14} />
                  ) : null}
                  {InputProps.endAdornment}
                </>
              ),
            },
            inputLabel: { ...InputLabelProps, shrink: true },
          },
        } as unknown as TextFieldProps;
        return <TextField {...tfProps} />;
      }}
    />
  );
}
